<template>
  <div ref="triggerEl" class="is-inline-block is-relative">
    <slot name="trigger" />
    <Teleport to="body" :disabled="!appendToBody">
      <div
        v-if="isOpen"
        ref="floatingEl"
        :style="floatingStyles"
        class="floating-panel-content xy-t-0 xy-l-0"
      >
        <slot />
      </div>
    </Teleport>
  </div>
</template>

<script>
import { computePosition, autoUpdate, offset, flip, shift } from '@floating-ui/vue';
import { FloatingPanelPlacement } from '@/modules/core/ui/constants/FloatingPanelPlacement';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'FloatingPanelView',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    placement: {
      type: String,
      validator(value) {
        return Object.values(FloatingPanelPlacement).includes(value);
      },
      default: FloatingPanelPlacement.BOTTOM_START
    },
    appendToBody: {
      type: Boolean,
      default: false
    },
    useFlip: {
      type: Boolean,
      default: true
    },
    offsetValue: {
      type: Number,
      default: 4
    },
    shiftPadding: {
      type: Number,
      default: 8
    }
  },
  data() {
    return {
      floatingStyles: {
        position: 'absolute',
        transform: 'translate(0px, 0px)'
      }
    };
  },
  watch: {
    isOpen(value) {
      if (value) {
        this.$nextTick(() => this.startAutoUpdate());
      } else {
        this.stopAutoUpdate();
      }
    }
  },
  methods: {
    startAutoUpdate() {
      this.stopAutoUpdate();
      const reference = this.$refs.triggerEl;
      const floating = this.$refs.floatingEl;
      if (!reference || !floating) {
        return;
      }
      this.cleanupAutoUpdate = autoUpdate(reference, floating, () => this.updatePosition());
    },
    stopAutoUpdate() {
      if (this.cleanupAutoUpdate) {
        this.cleanupAutoUpdate();
        this.cleanupAutoUpdate = null;
      }
    },
    async updatePosition() {
      const reference = this.$refs.triggerEl;
      const floating = this.$refs.floatingEl;
      if (!reference || !floating) {
        return;
      }
      const middleware = [offset(this.offsetValue)];
      if (this.useFlip) {
        middleware.push(flip());
      }
      middleware.push(shift({ padding: this.shiftPadding }));

      const { x, y, strategy } = await computePosition(reference, floating, {
        placement: this.placement,
        middleware
      });
      this.floatingStyles = {
        position: strategy,
        transform: `translate(${Math.round(x)}px, ${Math.round(y)}px)`
      };
    }
  },
  mounted() {
    if (this.isOpen) {
      this.startAutoUpdate();
    }
  },
  beforeUnmount() {
    this.stopAutoUpdate();
  }
};
</script>

<style scoped>
.floating-panel-content {
  z-index: var(--ce-z-index-notification);
}
</style>
